/**
 * PermissionButton Component
 *
 * A button component that automatically handles permission-based rendering
 * Uses reactstrap Button for consistency with the rest of the application
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import { usePermission } from '../../hooks/usePermission';

/**
 * Permission-aware Button Component
 *
 * @example Basic usage
 * <PermissionButton
 *   permission="ZONE_CREATE"
 *   variant="success"
 *   onClick={handleCreate}
 * >
 *   Create Zone
 * </PermissionButton>
 *
 * @example Disable instead of hide
 * <PermissionButton
 *   permission="ZONE_UPDATE"
 *   variant="warning"
 *   onClick={handleUpdate}
 *   strategy="disable"
 * >
 *   Update Zone
 * </PermissionButton>
 *
 * @example Multiple permissions with OR
 * <PermissionButton
 *   permission={['ZONE_UPDATE', 'ZONE_DELETE']}
 *   mode="any"
 *   variant="primary"
 * >
 *   Edit or Delete
 * </PermissionButton>
 */
export const PermissionButton = ({
  permission,
  mode = 'any',
  strategy = 'hide', // 'hide' or 'disable'
  disabledTooltip = 'You do not have permission',
  children,
  ...buttonProps
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermission();

  // Check permission - default to true if no permission specified
  let hasRequiredPermission = true;

  if (permission) {
    if (Array.isArray(permission)) {
      hasRequiredPermission = mode === 'all'
        ? hasAllPermissions(permission)
        : hasAnyPermission(permission);
    } else {
      hasRequiredPermission = hasPermission(permission);
    }
  }

  // If no permission and strategy is hide, don't render
  if (!hasRequiredPermission && strategy === 'hide') {
    return null;
  }

  // If no permission and strategy is disable, render disabled button
  if (!hasRequiredPermission && strategy === 'disable') {
    return (
      <Button
        {...buttonProps}
        disabled
        title={disabledTooltip}
        style={{
          ...buttonProps.style,
          cursor: 'not-allowed',
          opacity: 0.5,
        }}
      >
        {children}
      </Button>
    );
  }

  // Has permission, render normal button
  return (
    <Button {...buttonProps}>
      {children}
    </Button>
  );
};

PermissionButton.propTypes = {
  permission: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  mode: PropTypes.oneOf(['any', 'all']),
  strategy: PropTypes.oneOf(['hide', 'disable']),
  disabledTooltip: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default PermissionButton;
